import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, emptyBinding } from '../lib/api';
import { Badge, FailureBadge, IssueList, Loading, Toast, useAsync } from '../components/common';
import { FILE_TYPE_KEYS } from '../types';
import type {
  Brand,
  DspParent,
  FileTypeConfig,
  FileTypeKey,
  LambdaBinding,
  ParentLambdaRow,
  Principal,
  ValidationResult,
} from '../types';

const TABS = [
  ['identity', 'A · Identity'],
  ['buckets', 'B · Buckets'],
  ['files', 'C · File types'],
  ['brands', 'D · Brands & Zoho ids'],
  ['snowflake', 'E · Snowflake'],
  ['timing', 'F · Settlement timing'],
  ['integrations', 'G · Integrations'],
  ['keys', 'H · Key preview'],
  ['validation', 'I · Validation'],
  ['lambdas', 'J · Lambdas'],
] as const;

type TabId = (typeof TABS)[number][0];

export default function ParentDetailPage({ principal }: { principal: Principal }) {
  const { parentKey = '' } = useParams();
  const [tab, setTab] = useState<TabId>('identity');
  const [toast, setToast] = useState<{ message: string; tone: 'info' | 'error' } | null>(null);
  const { data, error, loading, reload, setData } = useAsync(
    () => api.getParent(parentKey),
    [parentKey],
  );

  const readOnly = principal.role !== 'admin';

  async function save(patch: Record<string, unknown>) {
    try {
      setData(await api.patchParent(parentKey, patch));
      setToast({ message: 'Saved.', tone: 'info' });
    } catch (err) {
      setToast({ message: (err as Error).message, tone: 'error' });
    }
  }

  if (loading || !data) return <Loading error={error} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>
            {data.display_name}{' '}
            <Badge tone={data.status === 'active' ? 'ok' : 'warn'}>{data.status}</Badge>
          </h1>
          <p>
            <code>{data.parent_key}</code> · Zoho <code>{data.dsp_parent_company_value}</code> ·
            token <code>{data.filename_token}</code> · bucket <code>{data.buckets.prod}</code>
          </p>
        </div>
        {!readOnly && (
          <div className="actions">
            {data.status !== 'active' ? (
              <button className="primary" onClick={() => save({ status: 'active' })}>
                Activate
              </button>
            ) : (
              <button onClick={() => save({ status: 'suspended' })}>Suspend</button>
            )}
          </div>
        )}
      </div>

      <div className="tabs">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            className={`tab ${tab === id ? 'active' : ''}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'identity' && <IdentityTab parent={data} readOnly={readOnly} onSave={save} />}
      {tab === 'buckets' && <BucketsTab parent={data} readOnly={readOnly} onSave={save} />}
      {tab === 'files' && <FileTypesTab parent={data} readOnly={readOnly} onSave={save} />}
      {tab === 'brands' && <BrandsTab parent={data} readOnly={readOnly} onSave={save} />}
      {tab === 'snowflake' && <SnowflakeTab parent={data} readOnly={readOnly} onSave={save} />}
      {tab === 'timing' && <TimingTab parent={data} readOnly={readOnly} onSave={save} />}
      {tab === 'integrations' && <IntegrationsTab parent={data} />}
      {tab === 'keys' && <KeysTab parentKey={parentKey} />}
      {tab === 'validation' && <ValidationTab parentKey={parentKey} />}
      {tab === 'lambdas' && (
        <LambdasTab
          parentKey={parentKey}
          readOnly={readOnly}
          onSaved={() => {
            reload();
            setToast({ message: 'Bindings saved.', tone: 'info' });
          }}
          onError={(m) => setToast({ message: m, tone: 'error' })}
        />
      )}

      {toast && <Toast {...toast} onDone={() => setToast(null)} />}
    </>
  );
}

/* ---------------------------------------------------------------- A */

function IdentityTab({
  parent,
  readOnly,
  onSave,
}: {
  parent: DspParent;
  readOnly: boolean;
  onSave: (patch: Record<string, unknown>) => void;
}) {
  const [aliases, setAliases] = useState(parent.aliases.join('\n'));
  const [displayName, setDisplayName] = useState(parent.display_name);
  const [token, setToken] = useState(parent.filename_token);
  const [parentCompany, setParentCompany] = useState(parent.dsp_parent_company_value);
  const [settlementEnabled, setSettlementEnabled] = useState(parent.settlement_enabled);
  const [lendapiEnabled, setLendapiEnabled] = useState(parent.lendapi_export_enabled);

  return (
    <div className="card">
      <h2>
        Identity
        <span className="hint">the routing key and every historical spelling of it</span>
      </h2>

      <div className="row">
        <label className="field">
          <span>Display name</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={readOnly}
          />
        </label>
        <label className="field">
          <span>Zoho DSP_Parent_Company value — what routing compares against</span>
          <input
            value={parentCompany}
            onChange={(e) => setParentCompany(e.target.value)}
            disabled={readOnly}
          />
        </label>
        <label className="field">
          <span>filename_token</span>
          <input value={token} onChange={(e) => setToken(e.target.value)} disabled={readOnly} />
        </label>
      </div>

      <label className="field">
        <span>Aliases, one per line</span>
        <textarea
          rows={6}
          value={aliases}
          onChange={(e) => setAliases(e.target.value)}
          disabled={readOnly}
        />
      </label>
      <p className="small muted">
        Matching folds case, hyphens and underscores, then compares exactly. Substring matching is
        deliberately not supported: it is what routed "Clarity Debt Relief" to JW in SM7 and to Debt
        Blue in SM3.
      </p>

      <h3>Participation</h3>
      <label className="inline">
        <input
          type="checkbox"
          checked={settlementEnabled}
          onChange={(e) => setSettlementEnabled(e.target.checked)}
          disabled={readOnly}
        />
        Sends settlement requests and receives confirmations
      </label>
      <label className="inline">
        <input
          type="checkbox"
          checked={lendapiEnabled}
          onChange={(e) => setLendapiEnabled(e.target.checked)}
          disabled={readOnly}
        />
        Exports to LendAPI
      </label>
      <p className="small muted">
        Clearing the settlement box marks the parent lead-only, which exempts it from the settlement
        bindings that activation otherwise requires. MLG is lead-only: it sends leads and takes
        funding receipts, but never completes a settlement round trip.
      </p>

      {!readOnly && (
        <div className="actions">
          <button
            className="primary"
            onClick={() =>
              onSave({
                display_name: displayName,
                dsp_parent_company_value: parentCompany,
                filename_token: token,
                settlement_enabled: settlementEnabled,
                lendapi_export_enabled: lendapiEnabled,
                aliases: aliases
                  .split('\n')
                  .map((a) => a.trim())
                  .filter(Boolean),
              })
            }
          >
            Save identity
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- B */

function BucketsTab({
  parent,
  readOnly,
  onSave,
}: {
  parent: DspParent;
  readOnly: boolean;
  onSave: (patch: Record<string, unknown>) => void;
}) {
  const [buckets, setBuckets] = useState(parent.buckets);

  return (
    <div className="card">
      <h2>Buckets</h2>
      <div className="row">
        <label className="field">
          <span>Production bucket</span>
          <input
            value={buckets.prod}
            onChange={(e) => setBuckets({ ...buckets, prod: e.target.value })}
            disabled={readOnly}
          />
        </label>
        <label className="field">
          <span>QA bucket</span>
          <input
            value={buckets.qa ?? ''}
            onChange={(e) => setBuckets({ ...buckets, qa: e.target.value || null })}
            disabled={readOnly}
          />
        </label>
      </div>
      <div className="row">
        <label className="field">
          <span>IAM policy name</span>
          <input
            value={buckets.iam_policy_name ?? ''}
            onChange={(e) => setBuckets({ ...buckets, iam_policy_name: e.target.value || null })}
            disabled={readOnly}
          />
        </label>
        <label className="field">
          <span>IAM user name</span>
          <input
            value={buckets.iam_user_name ?? ''}
            onChange={(e) => setBuckets({ ...buckets, iam_user_name: e.target.value || null })}
            disabled={readOnly}
          />
        </label>
      </div>

      <label className="inline">
        <input
          type="checkbox"
          checked={buckets.eventbridge_enabled}
          onChange={(e) => setBuckets({ ...buckets, eventbridge_enabled: e.target.checked })}
          disabled={readOnly}
        />
        Bucket-level EventBridge is enabled
      </label>

      <div className="callout warn" style={{ marginTop: 12 }}>
        This checkbox <strong>records</strong> the bucket's state; it does not change it. Bucket
        notification configuration is owned by <code>infra/s3_event_foundation</code>. Add the bucket
        there first, then tick this. An SM6 apply once wrote bucket notification config directly and
        wiped the shared bucket's EventBridge setting, silently taking SM0 and SM2 offline.
      </div>

      {!readOnly && (
        <div className="actions">
          <button className="primary" onClick={() => onSave({ buckets })}>
            Save buckets
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- C */

function FileTypesTab({
  parent,
  readOnly,
  onSave,
}: {
  parent: DspParent;
  readOnly: boolean;
  onSave: (patch: Record<string, unknown>) => void;
}) {
  const [fileTypes, setFileTypes] = useState<Record<string, FileTypeConfig>>(parent.file_types);

  function update(key: string, patch: Partial<FileTypeConfig>) {
    setFileTypes((current) => ({ ...current, [key]: { ...current[key], ...patch } }));
  }

  function add(key: FileTypeKey) {
    const inbound = ['client_table', 'creditor_table', 'transaction_table', 'settlement_request', 'funding_receipt', 'creditor_paid'];
    const stem = key.replace(/_/g, '-');
    update(key, {
      file_type_key: key,
      direction: inbound.includes(key) ? 'inbound' : 'outbound',
      folder: stem,
      filename_stem: stem,
      filename_template: `{date}-{token}-${stem}-{n}.csv`,
      include_token: true,
      enabled: true,
      immutable: false,
      immutable_reason: null,
    });
  }

  const missing = FILE_TYPE_KEYS.filter((k) => !(k in fileTypes));

  return (
    <div className="card">
      <h2>
        File types
        <span className="hint">
          {Object.keys(fileTypes).length} of 10 configured · folder and template are stored per
          parent, never inherited
        </span>
      </h2>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>File type</th>
              <th>Direction</th>
              <th>Folder</th>
              <th>Stem</th>
              <th>Filename template</th>
              <th>Enabled</th>
            </tr>
          </thead>
          <tbody>
            {FILE_TYPE_KEYS.filter((k) => k in fileTypes).map((key) => {
              const cfg = fileTypes[key];
              const locked = readOnly || cfg.immutable;
              return (
                <tr key={key}>
                  <td>
                    <code>{key}</code>
                    {cfg.immutable && (
                      <div className="small">
                        <Badge tone="info">immutable</Badge>
                        <div className="muted" style={{ marginTop: 3 }}>
                          {cfg.immutable_reason}
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge tone={cfg.direction === 'inbound' ? 'info' : 'default'}>
                      {cfg.direction}
                    </Badge>
                  </td>
                  <td>
                    <input
                      value={cfg.folder}
                      onChange={(e) => update(key, { folder: e.target.value })}
                      disabled={locked}
                    />
                  </td>
                  <td>
                    <input
                      value={cfg.filename_stem}
                      onChange={(e) => update(key, { filename_stem: e.target.value })}
                      disabled={locked}
                    />
                  </td>
                  <td style={{ minWidth: 300 }}>
                    <input
                      className="mono"
                      value={cfg.filename_template}
                      onChange={(e) => update(key, { filename_template: e.target.value })}
                      disabled={locked}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={cfg.enabled}
                      onChange={(e) => update(key, { enabled: e.target.checked })}
                      disabled={locked}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {missing.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div className="small muted" style={{ marginBottom: 6 }}>
            Not configured yet:
          </div>
          <div className="chips">
            {missing.map((key) => (
              <button key={key} onClick={() => add(key)} disabled={readOnly}>
                + {key}
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="small muted">
        Templates accept <code>{'{date}'}</code>, <code>{'{token}'}</code> and <code>{'{n}'}</code>{' '}
        only. <code>{'{date}'}</code> is the business day in America/Los_Angeles.
      </p>

      {!readOnly && (
        <div className="actions">
          <button className="primary" onClick={() => onSave({ file_types: fileTypes })}>
            Save file types
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- D */

function BrandsTab({
  parent,
  readOnly,
  onSave,
}: {
  parent: DspParent;
  readOnly: boolean;
  onSave: (patch: Record<string, unknown>) => void;
}) {
  const [brands, setBrands] = useState<Brand[]>(parent.brands);

  function update(index: number, patch: Partial<Brand>) {
    setBrands((current) => current.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  }

  return (
    <div className="card">
      <h2>
        Brands and Zoho DSP_Partner ids
        <span className="hint">a parent may have many brands; routing never uses them</span>
      </h2>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>brand_key</th>
              <th>Zoho DSP_Partner value</th>
              <th>Zoho record id</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Primary</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {brands.map((brand, index) => (
              <tr key={index}>
                <td>
                  <input
                    value={brand.brand_key}
                    onChange={(e) => update(index, { brand_key: e.target.value })}
                    disabled={readOnly}
                  />
                </td>
                <td>
                  <input
                    value={brand.dsp_partner_value}
                    onChange={(e) => update(index, { dsp_partner_value: e.target.value })}
                    disabled={readOnly}
                  />
                </td>
                <td>
                  <input
                    className="mono"
                    value={brand.zoho_partner_id ?? ''}
                    placeholder="unassigned"
                    onChange={(e) => update(index, { zoho_partner_id: e.target.value || null })}
                    disabled={readOnly}
                  />
                </td>
                <td>
                  <input
                    value={brand.phone ?? ''}
                    onChange={(e) => update(index, { phone: e.target.value || null })}
                    disabled={readOnly}
                  />
                </td>
                <td>
                  <input
                    value={brand.email ?? ''}
                    onChange={(e) => update(index, { email: e.target.value || null })}
                    disabled={readOnly}
                  />
                </td>
                <td>
                  <input
                    type="radio"
                    name="primary-brand"
                    checked={brand.is_primary}
                    onChange={() =>
                      setBrands((current) =>
                        current.map((b, i) => ({ ...b, is_primary: i === index })),
                      )
                    }
                    disabled={readOnly}
                  />
                </td>
                <td>
                  <button
                    className="danger"
                    onClick={() => setBrands((c) => c.filter((_, i) => i !== index))}
                    disabled={readOnly}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="small muted">
        SM6-create sources funded settlements from Zoho by these record ids. A brand with no id
        cannot be sourced from Zoho at all, only from its S3 settlement-confirmation file. SM7 reads
        the phone number into the boarding file's Debt Settlement Company Phone Number column.
      </p>

      {!readOnly && (
        <div className="actions">
          <button
            onClick={() =>
              setBrands((c) => [
                ...c,
                {
                  brand_key: '',
                  dsp_partner_value: '',
                  zoho_partner_id: null,
                  is_primary: c.length === 0,
                },
              ])
            }
          >
            Add brand
          </button>
          <button className="primary" onClick={() => onSave({ brands })}>
            Save brands
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- E */

function SnowflakeTab({
  parent,
  readOnly,
  onSave,
}: {
  parent: DspParent;
  readOnly: boolean;
  onSave: (patch: Record<string, unknown>) => void;
}) {
  const { data } = useAsync(() => api.listSnowflakeProfiles(), []);
  const [selected, setSelected] = useState(parent.snowflake_profile_key ?? '');
  const profile = useMemo(
    () => data?.find((p) => p.profile_key === selected) ?? null,
    [data, selected],
  );

  return (
    <div className="card">
      <h2>
        Snowflake profile
        <span className="hint">consumed by SM3, SM7, SM10, true-escrow and data-view</span>
      </h2>

      <label className="field" style={{ maxWidth: 380 }}>
        <span>Profile</span>
        <select value={selected} onChange={(e) => setSelected(e.target.value)} disabled={readOnly}>
          <option value="">— none —</option>
          {(data ?? []).map((p) => (
            <option key={p.profile_key} value={p.profile_key}>
              {p.profile_key}
              {p.exists_in_snowflake ? '' : ' (not created in Snowflake yet)'}
            </option>
          ))}
        </select>
      </label>

      {!selected && (
        <div className="callout danger">
          With no profile, SM3, SM7, SM10 and true-escrow fall back to the Debt Blue tables. The
          query succeeds, returns no rows for this partner, and ships a file with blank bank fields.
        </div>
      )}

      {profile && (
        <>
          {!profile.exists_in_snowflake && (
            <div className="callout warn">
              This profile is registered but its objects do not exist in Snowflake yet. Queries will
              error rather than return wrong data, which is the intended behaviour.
            </div>
          )}
          <dl className="kv">
            <dt>Database</dt>
            <dd className="mono">{profile.database}</dd>
            <dt>Schema</dt>
            <dd className="mono">{profile.schema_name}</dd>
            <dt>Role</dt>
            <dd className="mono">{profile.role}</dd>
            <dt>Client table</dt>
            <dd className="mono">{profile.client_table ?? '—'}</dd>
            <dt>Creditor table</dt>
            <dd className="mono">{profile.creditor_table ?? '—'}</dd>
            <dt>Transaction table</dt>
            <dd className="mono">{profile.txn_table ?? '—'}</dd>
            <dt>Escrow balance column</dt>
            <dd className="mono">{profile.escrow_balance_col}</dd>
            <dt>DS account / routing</dt>
            <dd className="mono">
              {profile.ds_acc_col} / {profile.ds_routing_col}
            </dd>
          </dl>
        </>
      )}

      {!readOnly && (
        <div className="actions" style={{ marginTop: 14 }}>
          <button
            className="primary"
            onClick={() => onSave({ snowflake_profile_key: selected || null })}
          >
            Save profile selection
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- F */

function TimingTab({
  parent,
  readOnly,
  onSave,
}: {
  parent: DspParent;
  readOnly: boolean;
  onSave: (patch: Record<string, unknown>) => void;
}) {
  const [timing, setTiming] = useState(parent.settlement_timing);
  return (
    <div className="card">
      <h2>Settlement timing</h2>
      <div className="row">
        <label className="field">
          <span>Days to first settlement</span>
          <input
            type="number"
            value={timing.first_settlement_days}
            onChange={(e) =>
              setTiming({ ...timing, first_settlement_days: Number(e.target.value) })
            }
            disabled={readOnly}
          />
        </label>
        <label className="field">
          <span>Days to last settlement</span>
          <input
            type="number"
            value={timing.last_settlement_days}
            onChange={(e) => setTiming({ ...timing, last_settlement_days: Number(e.target.value) })}
            disabled={readOnly}
          />
        </label>
        <label className="field">
          <span>Legal multiplier</span>
          <input
            type="number"
            step="0.05"
            value={timing.legal_multiplier}
            onChange={(e) => setTiming({ ...timing, legal_multiplier: Number(e.target.value) })}
            disabled={readOnly}
          />
        </label>
      </div>
      {!readOnly && (
        <div className="actions">
          <button className="primary" onClick={() => onSave({ settlement_timing: timing })}>
            Save timing
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- G */

function IntegrationsTab({ parent }: { parent: DspParent }) {
  return (
    <div className="card">
      <h2>
        Integrations
        <span className="hint">non-S3 pushes, such as the Clarity file-created webhook</span>
      </h2>
      {parent.integrations.length === 0 ? (
        <div className="callout info">No integrations configured for this parent.</div>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Key</th>
                <th>Kind</th>
                <th>Enabled</th>
                <th>URL secret</th>
                <th>Event type</th>
                <th>Allowed buckets</th>
              </tr>
            </thead>
            <tbody>
              {parent.integrations.map((i) => (
                <tr key={i.integration_key}>
                  <td>
                    <code>{i.integration_key}</code>
                  </td>
                  <td>{i.kind}</td>
                  <td>
                    {i.enabled ? <Badge tone="ok">enabled</Badge> : <Badge>disabled</Badge>}
                  </td>
                  <td className="mono small">{i.url_secret_ref ?? '—'}</td>
                  <td className="mono small">{i.event_type ?? '—'}</td>
                  <td className="mono small">{i.allowed_buckets.join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="small muted">
        The URL is stored as an SSM parameter reference, never as a literal. The bucket allowlist is
        the second gate: SM6-receipt fires its webhook only when the resolved group matches
        <em> and</em> the bucket is one of the partner's own.
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------- H */

function KeysTab({ parentKey }: { parentKey: string }) {
  const [environment, setEnvironment] = useState<'prod' | 'qa'>('prod');
  const { data, error, loading } = useAsync(
    () => api.parentKeys(parentKey, environment),
    [parentKey, environment],
  );

  return (
    <div className="card">
      <h2>
        Key preview
        <span className="hint">exactly what each Lambda will read or write today</span>
      </h2>

      <div className="actions" style={{ marginBottom: 12 }}>
        <button
          className={environment === 'prod' ? 'primary' : ''}
          onClick={() => setEnvironment('prod')}
        >
          Production
        </button>
        <button className={environment === 'qa' ? 'primary' : ''} onClick={() => setEnvironment('qa')}>
          QA
        </button>
      </div>

      {loading || !data ? (
        <Loading error={error} />
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>File type</th>
                <th>Direction</th>
                <th>S3 URI</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.file_type_key}>
                  <td>
                    <code>{row.file_type_key}</code>
                  </td>
                  <td>
                    <Badge tone={row.direction === 'inbound' ? 'info' : 'default'}>
                      {row.direction}
                    </Badge>
                  </td>
                  <td className="mono small">
                    {row.s3_uri ?? <span className="badge danger">{row.error}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- I */

function ValidationTab({ parentKey }: { parentKey: string }) {
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    try {
      setResult(await api.validateParent(parentKey));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>Validation</h2>
      <div className="actions" style={{ marginBottom: 14 }}>
        <button className="primary" onClick={run} disabled={busy}>
          {busy ? 'Checking…' : 'Run validation'}
        </button>
      </div>
      {result && <IssueList issues={result.issues} />}
    </div>
  );
}

/* ---------------------------------------------------------------- J */

const STATUS_TONE = {
  complete: 'ok',
  disabled: 'warn',
  missing: 'danger',
  unbound: 'default',
  not_applicable: 'default',
} as const;

function LambdasTab({
  parentKey,
  readOnly,
  onSaved,
  onError,
}: {
  parentKey: string;
  readOnly: boolean;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const { data, error, loading, reload } = useAsync(
    () => api.parentLambdas(parentKey),
    [parentKey],
  );
  const [draft, setDraft] = useState<Record<string, LambdaBinding> | null>(null);
  const [showAll, setShowAll] = useState(false);

  const rows = useMemo(() => {
    if (!data) return [];
    return showAll ? data : data.filter((r) => r.partner_aware);
  }, [data, showAll]);

  if (loading || !data) return <Loading error={error} />;

  const bindings = draft ?? Object.fromEntries(
    data.filter((r) => r.binding).map((r) => [r.lambda_key, r.binding as LambdaBinding]),
  );

  function update(row: ParentLambdaRow, patch: Partial<LambdaBinding>) {
    const current = bindings[row.lambda_key] ?? emptyBinding(parentKey, row.lambda_key);
    setDraft({ ...bindings, [row.lambda_key]: { ...current, ...patch } });
  }

  async function save() {
    try {
      await api.putParentBindings(
        parentKey,
        Object.values(bindings).filter(
          (b) => b.enabled || b.file_type_keys.length > 0 || b.notes,
        ),
      );
      setDraft(null);
      reload();
      onSaved();
    } catch (err) {
      onError((err as Error).message);
    }
  }

  return (
    <div className="card">
      <h2>
        Lambdas touching this parent
        <span className="hint">
          every function that reads or writes this parent's data, and the exact config it uses
        </span>
      </h2>

      <div className="actions" style={{ marginBottom: 12 }}>
        <label className="inline">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Show partner-agnostic modules too
        </label>
        {!readOnly && (
          <button className="primary" onClick={save} disabled={draft === null}>
            Save bindings
          </button>
        )}
      </div>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Lambda</th>
              <th>Trigger</th>
              <th>Direction</th>
              <th>Enabled</th>
              <th>File types</th>
              <th>EventBridge</th>
              <th>Resolved keys</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const binding = bindings[row.lambda_key];
              return (
                <tr key={row.lambda_key}>
                  <td style={{ minWidth: 190 }}>
                    <Link to={`/lambdas/${row.lambda_key}`}>{row.display_name}</Link>
                    <div className="small muted mono">{row.lambda_key}</div>
                    <div style={{ marginTop: 4 }} className="chips">
                      {row.required && <Badge tone="danger">required</Badge>}
                      <FailureBadge mode={row.failure_mode} />
                    </div>
                  </td>
                  <td className="small">{row.trigger_type}</td>
                  <td className="small">{row.direction}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={binding?.enabled ?? false}
                      onChange={(e) => update(row, { enabled: e.target.checked })}
                      disabled={readOnly || !row.partner_aware}
                    />
                  </td>
                  <td style={{ minWidth: 190 }}>
                    <div className="chips">
                      {FILE_TYPE_KEYS.map((key) => {
                        const on = binding?.file_type_keys.includes(key) ?? false;
                        return (
                          <button
                            key={key}
                            className={on ? 'primary' : ''}
                            style={{ padding: '1px 7px', fontSize: 11 }}
                            disabled={readOnly || !row.partner_aware}
                            onClick={() =>
                              update(row, {
                                file_type_keys: on
                                  ? (binding?.file_type_keys ?? []).filter((k) => k !== key)
                                  : [...(binding?.file_type_keys ?? []), key],
                              })
                            }
                          >
                            {key.replace(/_/g, '-')}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td style={{ minWidth: 160 }}>
                    <label className="inline">
                      <input
                        type="checkbox"
                        checked={binding?.eventbridge_enabled ?? false}
                        onChange={(e) => update(row, { eventbridge_enabled: e.target.checked })}
                        disabled={readOnly || !row.partner_aware}
                      />
                      on
                    </label>
                    {binding?.eventbridge_enabled && (
                      <input
                        className="mono"
                        style={{ marginTop: 4 }}
                        placeholder="settlement-request/"
                        value={binding.eventbridge_prefix ?? ''}
                        onChange={(e) =>
                          update(row, { eventbridge_prefix: e.target.value || null })
                        }
                        disabled={readOnly}
                      />
                    )}
                  </td>
                  <td className="mono small" style={{ minWidth: 240 }}>
                    {row.previews.length === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      row.previews.map((p) => (
                        <div key={p.file_type_key}>{p.s3_uri ?? `⚠ ${p.error}`}</div>
                      ))
                    )}
                  </td>
                  <td>
                    <Badge tone={STATUS_TONE[row.binding_status]}>{row.binding_status}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="small muted">
        Resolved keys are recomputed after saving. A binding for a file type this parent has not
        configured is a validation error, not a silent no-op.
      </p>
    </div>
  );
}
